import * as React from 'react'

import * as Sentry from '@sentry/browser'
import { GlobeIcon, MapIcon } from 'lucide-react'
import {
  action,
  comparer,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { ViewState } from 'react-map-gl/mapbox'

import ExportMapButton from '~/client/src/desktop/components/MapExport/ExportMapButton'
import OpacityBackground from '~/client/src/desktop/components/OpacityBackground/OpacityBackground'
import LocationAssociationPopover from '~/client/src/desktop/views/ProjectSetUp/components/AppsSitemap/components/dialogs/LocationAssociationPopover'
import LocationObjectsPallet from '~/client/src/desktop/views/ProjectSetUp/components/AppsSitemap/components/LocationObjectsPallet/LocationObjectsPallet'
import MapEditor from '~/client/src/desktop/views/ProjectSetUp/components/AppsSitemap/components/SitemapEditor/MapEditor'
import MapViewSetUpStore, {
  MapViewConfigType,
} from '~/client/src/desktop/views/ProjectSetUp/components/AppsSitemap/MapViewSetUp.store'
import {
  ACTION_KEY,
  DTO_ID_KEY,
  NEW_SITEMAP_TYPE_KEY,
  SITEMAP_ID_KEY,
} from '~/client/src/desktop/views/ProjectSetUp/components/AppsSitemap/models/ISitemapDelayedAction'
import { MapViewType } from '~/client/src/desktop/views/ProjectSetUp/components/AppsSitemap/stores/MapViewsList.store'
import DeleteDeliveryAttributeConfirmDialog from '~/client/src/desktop/views/ProjectSetUp/components/ProjectWorkflowsSetUp/components/DeliveryRequestSetUp/components/DeliveryDetailsConfigurations/components/DeleteDeliveryAttributeConfirmDialog'
import { Loader } from '~/client/src/shared/components/Loader'
import MapExportStore from '~/client/src/shared/components/MapExport/MapExport.store'
import { DeleteMapViewConfirmDialog } from '~/client/src/shared/components/MapViews/DeleteMapViewConfirmDialog'
import {
  buildMapViewItems,
  buildMapViewSubtitle,
  MapViewsPicker,
  type MapViewSource,
} from '~/client/src/shared/components/MapViews/MapViewsPicker'
import {
  IMapViewsSidebarView,
  MapViewsSidebar,
} from '~/client/src/shared/components/MapViews/MapViewsSidebar/MapViewsSidebar'
import { MapViewsSidebarCreateMenu } from '~/client/src/shared/components/MapViews/MapViewsSidebar/MapViewsSidebarCreateMenu'
import {
  IMapViewItem,
  IMapViewPublishedTo,
} from '~/client/src/shared/components/MapViews/MapViewsSidebar/MapViewsSidebarListItem'
import {
  ALL_PUBLISH_TO_APP_KEYS,
  PublishToAppKey,
} from '~/client/src/shared/components/MapViews/MapViewsSidebar/PublishToChipRow'
import { PublishToPopover } from '~/client/src/shared/components/MapViews/MapViewsSidebar/PublishToPopover'
import RenameMapViewDialog from '~/client/src/shared/components/MapViews/MapViewsSidebar/RenameMapViewDialog'
import { Input } from '~/client/src/shared/components/shadcn-ui/input'
import { Slider } from '~/client/src/shared/components/shadcn-ui/slider'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import GlobeView from '~/client/src/shared/models/GlobeView'
import Sitemap from '~/client/src/shared/models/Sitemap'
import BasemapsStore from '~/client/src/shared/stores/domain/Basemaps.store'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import LocationIntegrationsStore from '~/client/src/shared/stores/domain/LocationIntegrations.store'
import type ProjectResourcesStore from '~/client/src/shared/stores/domain/ProjectResources.store'
import RemoteTilesetsStore from '~/client/src/shared/stores/domain/RemoteTilesets.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import SyncRestrictionsStore from '~/client/src/shared/stores/domain/SyncRestrictions.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import TilesetsStore from '~/client/src/shared/stores/domain/Tilesets.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import { DEFAULT_NAME } from '~/client/src/shared/stores/GlobeViewControl.store'
import { toast } from '~/client/src/shared/utils/toast'

import DesktopEventsStore from '../../stores/EventStore/DesktopEvents.store'
import DesktopCommonStore from '../../stores/ui/DesktopCommon.store'
import DeleteTilesetConfirmDialog from '../../views/ProjectSetUp/components/AppsSitemap/components/dialogs/DeleteTilesetConfirmDialog/DeleteTilesetConfirmDialog'
import PdfConverterDialog from '../../views/ProjectSetUp/components/AppsSitemap/components/dialogs/PdfConverterDialog'
import UpdateSitemapDialog from '../../views/ProjectSetUp/components/AppsSitemap/components/dialogs/UpdateSitemapDialog/UpdateSitemapDialog'
import UploadMapFileDialog from '../../views/ProjectSetUp/components/AppsSitemap/components/dialogs/UploadMapFileDialog/UploadMapFileDialog'
import UploadRemoteTilesetDialog from '../../views/ProjectSetUp/components/AppsSitemap/components/dialogs/UploadRemoteTilesetDialog/UploadRemoteTilesetDialog'
import GlobeLeftPanel from '../../views/ProjectSetUp/components/AppsSitemap/components/GlobeLeftPanel/GlobeLeftPanel'
import GlobeLimitedRibbon from '../../views/ProjectSetUp/components/AppsSitemap/components/ribbons/GlobeLimitedRibbon'
import SitemapRibbon from '../../views/ProjectSetUp/components/AppsSitemap/components/ribbons/SitemapRibbon'
import SaveAttributeConfirmDialog from '../../views/ProjectSetUp/components/ProjectWorkflowsSetUp/components/DeliveryRequestSetUp/components/DeliveryDetailsConfigurations/components/SaveGlobeItemConfirmDialog'
import ProjectSetUpPageStore from '../../views/ProjectSetUp/ProjectSetUpPage.store'
import GlobeProperties from './GlobeProperties'
import { createMapSetupExportEntry } from './mapSetupExportEntry'

import './GeneralMapViewSetUp.scss'

interface IProps {
  selectedGlobeId?: string
  globes?: GlobeView[]
  projectSetUpPageStore?: ProjectSetUpPageStore
  eventsStore?: DesktopEventsStore
  sitemapsStore?: SitemapsStore
  basemapsStore?: BasemapsStore
  locationAttributesStore?: LocationAttributesStore
  sitemapItemsStore?: SitemapItemsStore
  fileUploadingStore?: FileUploadingStore
  common?: DesktopCommonStore
  syncRestrictionsStore?: SyncRestrictionsStore
  userProjectsStore?: UserProjectsStore
  tagsStore?: TagsStore
  globeViewsStore?: GlobeViewsStore
  locationIntegrationsStore?: LocationIntegrationsStore
  tilesetsStore?: TilesetsStore
  remoteTilesetsStore?: RemoteTilesetsStore
  locationResourcesStore?: Pick<
    ProjectResourcesStore<unknown, unknown>,
    'propertiesStateFor'
  >

  renderChildren?: (width: number, height: number) => JSX.Element
}

const ExportPreview = React.lazy(
  () => import('~/client/src/shared/components/MapExport/ExportPreview'),
)

const transparency = 'transparency'
const sitemapsDataIsLoading = 'Sitemaps Data is loading...'

const DELAYED_ACTION_QUERY_PARAMS = [
  ACTION_KEY,
  NEW_SITEMAP_TYPE_KEY,
  DTO_ID_KEY,
  SITEMAP_ID_KEY,
]

const APP_TO_CONFIG: Partial<Record<PublishToAppKey, MapViewConfigType>> = {
  home: MapViewConfigType.logistics,
  workflows: MapViewConfigType.forms,
  deliveries: MapViewConfigType.deliveries,
}

const EMPTY_PUBLISHED_TO: IMapViewPublishedTo = Object.fromEntries(
  ALL_PUBLISH_TO_APP_KEYS.map(key => [key, false]),
) as unknown as IMapViewPublishedTo

// The manager opens grouped by the location breakdown structure; the other surfaces keep
// their ungrouped default.
const MAP_VIEWS_MANAGER_INITIAL_VIEW: IMapViewsSidebarView = { group: 'lbs' }

@inject(
  'eventsStore',
  'sitemapsStore',
  'basemapsStore',
  'locationAttributesStore',
  'sitemapItemsStore',
  'fileUploadingStore',
  'common',
  'syncRestrictionsStore',
  'userProjectsStore',
  'tagsStore',
  'globeViewsStore',
  'tilesetsStore',
  'remoteTilesetsStore',
  'locationResourcesStore',
)
@observer
export default class GeneralMapViewSetUp extends React.Component<IProps> {
  @observable private isSavingGlobe: boolean = false
  @observable private renamingMapViewId: string | null = null
  @observable private publishToTargetId: string | null = null
  @observable.ref private publishToAnchor: HTMLElement | null = null
  // Frozen target so canvas pagination mid-interaction can't retarget Apply.
  @observable private locationTargetMapId: string | null = null
  @observable.ref private locationTargetAnchor: HTMLElement | null = null
  // Portal nested dialogs inside the sidebar's Radix focus trap so the rename input can receive keys.
  private nestedDialogPortalRef = React.createRef<HTMLDivElement>()
  private readonly globeViewsStore: GlobeViewsStore
  private readonly locationAttributesStore: LocationAttributesStore
  private store: MapViewSetUpStore
  private readonly mapExportStore = new MapExportStore()
  public constructor(props: IProps) {
    super(props)

    this.globeViewsStore = props.globeViewsStore
    this.locationAttributesStore = props.locationAttributesStore
    this.store = new MapViewSetUpStore(
      props.eventsStore,
      props.sitemapsStore,
      props.basemapsStore,
      props.locationAttributesStore,
      props.sitemapItemsStore,
      props.fileUploadingStore,
      props.syncRestrictionsStore,
      props.userProjectsStore,
      props.tagsStore,
      props.globeViewsStore,
      props.tilesetsStore,
      props.remoteTilesetsStore,
      props.locationResourcesStore,
    )

    const { lastEditedGlobe } = this.store.globeViewSetupStore
    this.store.mapBoxEditorStore.isRubberMode = false
    const globe = props.globeViewsStore.byId.get(props.selectedGlobeId)
    const globes = props.globeViewsStore.list
    this.store.globeViewSetupStore.selectInitialGlobe(
      globe || lastEditedGlobe || globes?.[0],
    )
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: Readonly<IProps>): void {
    if (this.props.selectedGlobeId !== prevProps.selectedGlobeId) {
      this.store.globeViewSetupStore.selectGlobeById(this.props.selectedGlobeId)
      this.store.mapBoxViewerStore.setViewportFromAddress()
    }
  }

  public componentDidMount(): void {
    this.store.sitemapsSetupStore.setViewportFromAddress()
    this.mapExportStore.setEntry(
      createMapSetupExportEntry(
        this.store,
        this.props.eventsStore.appState,
        () => this.isSavingGlobe,
      ),
    )
    // The page swaps itself for a loader while project data reloads, taking the live map the export
    // reads with it, so the export closes first.
    this.exportLoaderDisposer = reaction(
      () => this.store.isInitialLoaderShown || this.store.isLoading,
      isLoaderShown => isLoaderShown && this.mapExportStore.close(),
    )
    // Capture/clear popover target across both right-panel and sidebar open paths
    // so canvas pagination can't retarget Apply.
    this.assignDialogReactionDisposer = reaction(
      () => ({
        globeOpen: this.store.globeViewSetupStore.isAssignGlobeDialogShown,
        sitemapOpen: this.store.sitemapsSetupStore.isAssignSitemapDialogShown,
      }),
      (curr, prev) => {
        const anyOpenedNow = curr.globeOpen || curr.sitemapOpen
        const anyOpenedPrev = prev.globeOpen || prev.sitemapOpen
        if (anyOpenedNow && !anyOpenedPrev) {
          this.captureLocationTargetFromSelection()
        } else if (!anyOpenedNow && anyOpenedPrev) {
          this.clearLocationTarget()
        }
      },
    )
    // Never leave the open picker on "0 of N": when the visible list or selection
    // changes such that nothing valid is selected, re-select the map view at the
    // last selected index (clamped). The index is into the source order, so a delete
    // lands on the neighbouring card only while the manager is ungrouped and in its
    // default sort; grouped by LBS (its default) the re-selected view can sit in
    // another band.
    // Deferred because selectSitemap transiently nulls selection mid-update;
    // structural-equals fires only on real id/selection changes (not on churn),
    // and tracking ids (not counts) catches same-count swaps. globeViewsStore is
    // captured locally because mobx-react forbids reading this.props in a reaction.
    const { globeViewsStore } = this.props
    this.mapViewSelectionGuardDisposer = reaction(
      () => {
        const { globeViewSetupStore, sitemapsSetupStore } = this.store
        return {
          open: this.store.isGlobeListShown,
          ids: [
            ...globeViewsStore.list.map(g => g.id),
            ...sitemapsSetupStore.whiteboards.map(s => s.id),
          ],
          selectedId:
            globeViewSetupStore.selectedGlobeView?.id ||
            sitemapsSetupStore.selectedSitemap?.id,
        }
      },
      ({ open }) => {
        clearTimeout(this.selectionGuardTimer)
        if (!open) return
        this.selectionGuardTimer = setTimeout(() => {
          const items = this.mapViewsSidebarItems
          if (!items.length) return
          const selectedId = this.selectedMapViewSidebarId
          const selectedIdx = selectedId
            ? items.findIndex(i => i.id === selectedId)
            : -1
          if (selectedIdx >= 0) {
            this.lastSelectedMapViewIndex = selectedIdx
            return
          }
          const fallbackIdx = Math.min(
            this.lastSelectedMapViewIndex,
            items.length - 1,
          )
          this.selectMapViewById(items[fallbackIdx].id)
        }, 0)
      },
      { fireImmediately: true, equals: comparer.structural },
    )
  }

  public componentWillUnmount(): void {
    this.mapExportStore.setEntry(null)
    this.mapExportStore.close()
    this.exportLoaderDisposer?.()
    this.assignDialogReactionDisposer?.()
    this.mapViewSelectionGuardDisposer?.()
    clearTimeout(this.selectionGuardTimer)
  }

  private assignDialogReactionDisposer?: IReactionDisposer
  private exportLoaderDisposer?: IReactionDisposer
  private mapViewSelectionGuardDisposer?: IReactionDisposer
  private selectionGuardTimer?: ReturnType<typeof setTimeout>
  private lastSelectedMapViewIndex = 0

  @action.bound
  private captureLocationTargetFromSelection(): void {
    if (this.locationTargetMapId) return
    const { globeViewSetupStore, sitemapsSetupStore } = this.store
    const selected = globeViewSetupStore.isAssignGlobeDialogShown
      ? globeViewSetupStore.selectedGlobeView
      : sitemapsSetupStore.isAssignSitemapDialogShown
        ? sitemapsSetupStore.selectedSitemap
        : null
    if (selected) this.locationTargetMapId = selected.id
  }

  public UNSAFE_componentWillMount(): void {
    const { search, pathname } = this.props.common.history.location
    const queryParams = new URLSearchParams(search)

    const { sitemapsSetupStore } = this.store
    sitemapsSetupStore.setDelayedActionParamsFromUrl(queryParams)

    DELAYED_ACTION_QUERY_PARAMS.forEach(param => {
      queryParams.delete(param)
    })
    const path = pathname + '?' + queryParams.toString()
    // @ts-ignore needed here
    this.props.common.history.replace(path)
    this.store.mapBoxEditorStore.isRubberMode = false
  }

  public render(): JSX.Element {
    const { projectSetUpPageStore, globes, eventsStore } = this.props
    const {
      isInitialLoaderShown,
      mapViewItemsSetupStore,
      sitemapsSetupStore: { selectedSitemap },
      isLoading,
      globeViewSetupStore: { updateGlobeView, selectedGlobeView },
      isGlobeMode,
      currentStep,
    } = this.store

    const isGlobeLoading = eventsStore.appState.loading.get(e.SAVE_GLOBE_VIEW)
    if (isInitialLoaderShown) {
      return <Loader hint={sitemapsDataIsLoading} />
    }

    if (isLoading) {
      return <Loader hint={Localization.translator.loading} />
    }

    const {
      deletableMapViewItem,
      deletableSitemapItemCaption,
      mapViewItemForSave,
      mapViewItemForSaveCaption,
      applyMapViewItemDeleteConfirmDialog,
      hideMapViewItemDeleteConfirmDialog,
      applyMapViewItemSaveConfirmDialog,
      hideMapViewItemSaveConfirmDialog,
      selectedMapViewItem,
      isSavingAfterChanges,
    } = mapViewItemsSetupStore

    return (
      <div className="general-map-setup globe-view global-map-setup full-height">
        <PdfConverterDialog store={this.store} />
        {this.renderMapViewsSidebar()}
        <SitemapRibbon store={this.store} shouldHideButton={true} />
        {this.store.sitemapControlStore.shouldShowCreateMapFileMenu && (
          <>
            <OpacityBackground isAnyPopupOpened={true} />
            <UploadMapFileDialog
              mapViewItemsSetupStore={this.store.mapViewItemsSetupStore}
              store={this.store.sitemapControlStore}
            />
          </>
        )}
        {this.store.sitemapControlStore.shouldShowCreateRemoteTilesetMenu && (
          <>
            <OpacityBackground isAnyPopupOpened={true} />
            <UploadRemoteTilesetDialog
              store={this.store.sitemapControlStore}
              shouldFixTopPosition={false}
              mapViewItemsSetupStore={this.store.mapViewItemsSetupStore}
            />
          </>
        )}
        {this.store.sitemapControlStore.shouldShowEditBasemapMenu && (
          <>
            <OpacityBackground isAnyPopupOpened={true} />
            <UpdateSitemapDialog store={this.store} />
          </>
        )}
        {this.renderDeleteMapViewConfirmDialog()}
        <DeleteTilesetConfirmDialog store={this.store} />
        <DeleteTilesetConfirmDialog store={this.store} isRemote />
        {deletableMapViewItem && (
          <DeleteDeliveryAttributeConfirmDialog
            isShown={!!deletableMapViewItem}
            isSubTextHidden={
              deletableMapViewItem && !deletableMapViewItem.dataObject
            }
            type={deletableSitemapItemCaption}
            name={deletableMapViewItem.displayName}
            onApply={applyMapViewItemDeleteConfirmDialog}
            onHide={hideMapViewItemDeleteConfirmDialog}
          />
        )}
        {mapViewItemForSave && (
          <SaveAttributeConfirmDialog
            isShown={!!mapViewItemForSave}
            type={mapViewItemForSaveCaption}
            name={mapViewItemForSave.displayName}
            onApply={applyMapViewItemSaveConfirmDialog}
            onHide={hideMapViewItemSaveConfirmDialog}
            isSaving={isSavingAfterChanges}
          />
        )}
        <div className={classList({ 'main-area row full-height': true })}>
          <div className="col full-height">
            <div className="row sitemap-edit-content full-height">
              <GlobeLeftPanel
                store={this.store}
                projectSetUpPageStore={projectSetUpPageStore}
                step={currentStep}
                globes={globes}
                isDisabled={isGlobeLoading}
                selectedMapViewItem={selectedMapViewItem}
                selectedSitemap={selectedSitemap}
              />
              {this.renderContent()}
              {(selectedGlobeView || selectedSitemap) && (
                <GlobeProperties
                  projectSetUpPageStore={projectSetUpPageStore}
                  globe={selectedGlobeView}
                  store={this.store}
                  updateGlobeView={updateGlobeView}
                  isItemSelected={!!selectedMapViewItem}
                  selectedSitemap={selectedSitemap}
                  saveAlignment={this.saveAlignment}
                  exitRubber={this.exitRubberMode}
                  isDisabled={isGlobeLoading}
                  isGlobeMode={isGlobeMode}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  @action.bound
  private saveAlignment(): void {
    const {
      mapBoxViewerStore: {
        width,
        height,
        globeCorners,
        setViewportFromAddress,
      },
      sitemapsSetupStore: {
        selectedSitemap,
        updateGeoposition,
        deselectSitemap,
      },
      setStep,
      globeViewSetupStore,
    } = this.store

    updateGeoposition(
      selectedSitemap,
      this.viewport,
      width,
      height,
      globeCorners,
    )

    setStep(null)
    deselectSitemap()

    if (
      !globeViewSetupStore.selectedGlobeView.sitemapsMap[selectedSitemap.id]
    ) {
      globeViewSetupStore.setSitemapToGlobeView(selectedSitemap.id)
    }

    globeViewSetupStore.selectGlobe(globeViewSetupStore.selectedGlobeView)
    setViewportFromAddress()
  }

  @action.bound
  private exitRubberMode(): void {
    const {
      globeViewSetupStore,
      sitemapsSetupStore: { deselectSitemap },
      mapBoxViewerStore,
      mapBoxEditorStore,
      setStep,
    } = this.store

    mapBoxEditorStore.isRubberMode = false

    setStep(null)
    deselectSitemap()
    globeViewSetupStore.selectGlobe(globeViewSetupStore.selectedGlobeView)
    mapBoxViewerStore.setViewportFromAddress()
  }

  private renderContent(): JSX.Element {
    const { eventsStore, renderChildren } = this.props
    const {
      mapBoxEditorStore: {
        setOpacity,
        opacity,
        isRubberMode,
        isMarkerDragging,
      },
      mapBoxViewerStore: { isResetDisabled },
      globeViewSetupStore,
      isGlobeMode,
    } = this.store
    const isLoading = eventsStore.appState.loading.get(e.SAVE_GLOBE_VIEW)
    const globe = globeViewSetupStore?.selectedGlobeView

    return (
      <div
        className={classList({
          'main-area-container full-height relative global-map': true,
          'tw:!cursor-grabbing tw:[&_*]:!cursor-grabbing': isMarkerDragging,
        })}
      >
        <div
          className={classList({
            'top-view-row full-width tw:@container tw:pointer-events-none': true,
            'tw:[&>*]:pointer-events-auto': !isMarkerDragging,
          })}
        >
          {this.renderMapViewsPicker()}
          {isGlobeMode && (
            <GlobeLimitedRibbon
              createNewGlobe={this.createNewView}
              saveSelectedGlobe={this.updateGlobeView}
              isSavingGlobe={this.isSavingGlobe}
              resetViewport={this.resetViewport}
              isResetDisabled={isResetDisabled}
            >
              <ExportMapButton mapExportStore={this.mapExportStore} />
            </GlobeLimitedRibbon>
          )}
        </div>
        {!isLoading && !isRubberMode && (
          <LocationObjectsPallet store={this.store} />
        )}
        <div className="sitemap-editor-container row full-height">
          {isLoading && !globe ? (
            <Loader />
          ) : (
            <MapEditor
              store={this.store}
              opacity={this.store.mapBoxEditorStore.opacity}
              renderChildren={renderChildren}
              isRubberMode={this.store.mapBoxEditorStore.isRubberMode}
            />
          )}
        </div>
        {isRubberMode && (
          <div className="tw:absolute tw:bottom-[115px] tw:z-[1000] tw:translate-x-full tw:rounded-[10px] tw:bg-popover tw:p-1.5 tw:shadow-md">
            <div className="tw:text-label-md-caps-prominent tw:uppercase tw:text-muted-foreground">
              {transparency}
            </div>
            <div className="tw:flex tw:items-center">
              <MapIcon
                size={16}
                className="tw:mr-2.5 tw:cursor-pointer tw:text-muted-foreground"
                onClick={setOpacity.bind(null, 0)}
              />
              <Slider
                className="tw:mr-2.5 tw:h-6 tw:w-[100px] tw:[&[data-dragging]]:cursor-grabbing tw:[&_[data-slot=slider-thumb]]:cursor-grab tw:[&_[data-slot=slider-thumb][data-dragging]]:cursor-grabbing"
                thumbAlignment="edge"
                min={0}
                max={100}
                value={opacity}
                onValueChange={setOpacity}
                aria-label={transparency}
              />
              <GlobeIcon
                size={16}
                className="tw:mr-5 tw:cursor-pointer tw:text-muted-foreground"
                onClick={setOpacity.bind(null, 100)}
              />
              <Input
                inputMode="numeric"
                value={opacity}
                onChange={this.updateOpacity}
                onKeyDown={this.stepOpacity}
                aria-label={transparency}
                className="tw:h-7 tw:w-12 tw:px-1.5 tw:text-center"
              />
              <span className="tw:ml-1 tw:text-body-sm tw:text-muted-foreground">
                %
              </span>
            </div>
          </div>
        )}
        {this.mapExportStore.isOpen && (
          <React.Suspense fallback={null}>
            <ExportPreview
              exportStore={this.mapExportStore}
              liveSetUpStore={this.store}
            />
          </React.Suspense>
        )}
      </div>
    )
  }

  private renderMapViewsPicker(): JSX.Element {
    const { locationAttributesStore } = this.props
    const { globeViewSetupStore, sitemapsSetupStore, toggleGlobeList } =
      this.store

    const selectedGlobe = globeViewSetupStore.selectedGlobeView
    const selectedSitemap = sitemapsSetupStore.selectedSitemap
    const title = selectedGlobe?.name || selectedSitemap?.name || DEFAULT_NAME

    const subtitle = buildMapViewSubtitle({
      globe: selectedGlobe,
      sitemap: selectedSitemap,
      locationAttributesStore,
    })

    const { total, combinedIdx } = this.getCombinedMapIndex()

    return (
      <MapViewsPicker.Root className="tw:max-w-[361px]">
        <MapViewsPicker.Trigger
          title={title}
          subtitle={subtitle}
          isOpen={this.store.isGlobeListShown}
          onClick={toggleGlobeList}
        />
        <MapViewsPicker.Pagination
          currentIndex={combinedIdx + 1}
          total={total}
          onPrev={this.selectPrevMapInSetup}
          onNext={this.selectNextMapInSetup}
        />
      </MapViewsPicker.Root>
    )
  }

  private selectPrevMapInSetup = (): void => this.cycleMapInSetup(-1)
  private selectNextMapInSetup = (): void => this.cycleMapInSetup(1)

  @computed
  private get mapViewsSidebarItems(): IMapViewItem[] {
    const { globeViewsStore, locationAttributesStore } = this
    const { sitemapsSetupStore } = this.store
    // Store throws "Last globe cannot be deleted" when only one remains.
    const canDeleteGlobe = globeViewsStore.list.length > 1
    const sources: MapViewSource[] = [
      ...globeViewsStore.list.map((map): MapViewSource => ({
        kind: 'globe',
        map,
      })),
      ...sitemapsSetupStore.whiteboards.map((map): MapViewSource => ({
        kind: 'sitemap',
        map,
      })),
    ]

    return buildMapViewItems({
      sources,
      locationAttributesStore,
      publishedTo: source =>
        source.kind === 'globe'
          ? this.resolveGlobePublishedTo(source.map.id)
          : this.resolveSitemapPublishedTo(source.map.id),
    }).map((item, index) =>
      sources[index].kind === 'globe'
        ? { ...item, canDelete: canDeleteGlobe }
        : item,
    )
  }

  private resolveGlobePublishedTo = (id: string): IMapViewPublishedTo => ({
    home: !!this.store.logisticsAssignedGlobes[id],
    workflows: !!this.store.formsAssignedGlobes[id],
    deliveries: !!this.store.deliveriesAssignedGlobes[id],
    schedule: false,
  })

  private resolveSitemapPublishedTo = (id: string): IMapViewPublishedTo => ({
    home: !!this.store.logisticsAssignedSitemaps[id],
    workflows: !!this.store.formsAssignedSitemaps[id],
    deliveries: !!this.store.deliveriesAssignedSitemaps[id],
    schedule: false,
  })

  @computed
  private get selectedMapViewSidebarId(): string | undefined {
    const { globeViewSetupStore, sitemapsSetupStore } = this.store
    return (
      globeViewSetupStore.selectedGlobeView?.id ||
      sitemapsSetupStore.selectedSitemap?.id
    )
  }

  private selectMapViewById = (id: string): boolean => {
    const { globe, sitemap } = this.resolveMapViewById(id)
    if (globe) {
      this.store.selectMapView(MapViewType.GlobeView, id)
    } else if (sitemap) {
      this.store.selectMapView(MapViewType.Whiteboard, id)
    } else {
      return false
    }
    return true
  }

  private handleSelectMapViewFromSidebar = (id: string): void => {
    if (!this.selectMapViewById(id)) return
    this.handleMapViewsSidebarOpenChange(false)
  }

  private handleMapViewsSidebarOpenChange = (open: boolean): void => {
    if (open === this.store.isGlobeListShown) return
    // Anchors live inside the sidebar — clear nested overlays before unmount to avoid detached-anchor floating UI.
    if (!open) this.closeNestedOverlays()
    this.store.toggleGlobeList()
  }

  @action.bound
  private closeNestedOverlays(): void {
    this.renamingMapViewId = null
    this.publishToTargetId = null
    this.publishToAnchor = null
    this.store.globeViewSetupStore.hideAssignGlobeDialog()
    this.store.sitemapsSetupStore.hideAssignSitemapDialog()
    // Confirm dialog portals into the manager; leaving it open detaches its portal target.
    this.store.globeViewSetupStore.deselectDeletableGlobe()
    this.store.sitemapsSetupStore.hideSitemapDeleteConfirmDialog()
    this.clearLocationTarget()
  }

  private get availableApps(): PublishToAppKey[] {
    const appState = this.props.eventsStore?.appState
    if (!appState) return []
    const apps: PublishToAppKey[] = []
    if (!appState.isLogisticsDisabled) apps.push('home')
    if (!appState.isWorkflowsDisabled) apps.push('workflows')
    if (!appState.isDeliveriesDisabled) apps.push('deliveries')
    return apps
  }

  @action.bound
  private handleOpenPublishTo(id: string, anchor: HTMLElement): void {
    this.publishToTargetId = id
    this.publishToAnchor = anchor
  }

  @action.bound
  private handleClosePublishTo(): void {
    this.publishToTargetId = null
    this.publishToAnchor = null
  }

  // Diff scope intentionally limited to `availableApps` to avoid silently unpublishing hidden apps.
  @action.bound
  private handleApplyPublishTo(next: IMapViewPublishedTo): void {
    const id = this.publishToTargetId
    if (!id) return
    const { globe, sitemap } = this.resolveMapViewById(id)
    if (!globe && !sitemap) return
    const current = globe
      ? this.resolveGlobePublishedTo(id)
      : this.resolveSitemapPublishedTo(id)
    this.availableApps
      .filter(key => next[key] !== current[key])
      .forEach(key => {
        const config = APP_TO_CONFIG[key]
        if (config === undefined) return
        if (globe) {
          this.store.onGlobeSectionClick(globe, config)
        } else if (sitemap) {
          this.store.onWhiteboardSectionClick(sitemap, config)
        }
      })
  }

  private get publishToCurrent(): IMapViewPublishedTo {
    const id = this.publishToTargetId
    if (!id) return EMPTY_PUBLISHED_TO
    const { globe } = this.resolveMapViewById(id)
    return globe
      ? this.resolveGlobePublishedTo(id)
      : this.resolveSitemapPublishedTo(id)
  }

  private resolveMapViewById(id: string): {
    globe?: GlobeView
    sitemap?: Sitemap
  } {
    const { globeViewsStore, sitemapsStore } = this.props
    const globe = globeViewsStore.byId.get(id)
    if (globe) return { globe }
    const sitemap = sitemapsStore.byId.get(id)
    return sitemap && !sitemap.isReferenced ? { sitemap } : {}
  }

  @action.bound
  private handleRenameMapView(id: string): void {
    // Drawer stays open; SheetSurface ignores outside-press while a Dialog is open.
    this.renamingMapViewId = id
  }

  @action.bound
  private cancelRenameMapView(): void {
    this.renamingMapViewId = null
  }

  @action.bound
  private submitRenameMapView(newName: string): void {
    if (!this.renamingMapViewId) return
    const { globe, sitemap } = this.resolveMapViewById(this.renamingMapViewId)
    if (globe) {
      this.store.globeViewSetupStore.updateGlobeName(globe, newName)
    } else if (sitemap) {
      this.store.sitemapsSetupStore.updateSitemapName(sitemap, newName)
    }
    this.renamingMapViewId = null
  }

  @action.bound
  private handleCreateNewView = (): void => {
    this.handleMapViewsSidebarOpenChange(false)
    this.createNewView()
  }

  @action.bound
  private handleCreateNewWhiteboard = (): void => {
    this.handleMapViewsSidebarOpenChange(false)
    this.store.sitemapControlStore.toggleCreateWhiteboardMenu()
  }

  @action.bound
  private handleDeleteMapView(id: string): void {
    const { globe, sitemap } = this.resolveMapViewById(id)
    if (globe) {
      this.store.globeViewSetupStore.selectDeletableGlobe(globe)
    } else if (sitemap) {
      this.store.sitemapsSetupStore.showDeleteConfirmationDialogBySitemap(
        sitemap,
      )
    }
  }

  @action.bound
  private async handleCopyMapView(id: string): Promise<void> {
    const { globe, sitemap } = this.resolveMapViewById(id)
    if (globe) {
      await this.store.globeViewSetupStore.copyGlobe(globe)
    } else if (sitemap) {
      await this.store.sitemapsSetupStore.duplicateSitemap(sitemap.id)
    }
  }

  @action.bound
  private handleSetLocationForMapView(id: string, anchor: HTMLElement): void {
    // Explicit set (not toggle) because the popover is shared between globe and sitemap rows.
    const { globe, sitemap } = this.resolveMapViewById(id)
    if (!globe && !sitemap) return
    const { globeViewSetupStore, sitemapsSetupStore } = this.store
    this.locationTargetMapId = id
    this.locationTargetAnchor = anchor
    if (globe) {
      sitemapsSetupStore.hideAssignSitemapDialog()
      globeViewSetupStore.showAssignGlobeDialog()
    } else {
      globeViewSetupStore.hideAssignGlobeDialog()
      sitemapsSetupStore.showAssignSitemapDialog()
    }
  }

  private get locationTarget(): {
    globe?: GlobeView
    sitemap?: Sitemap
  } {
    if (!this.locationTargetMapId) return {}
    return this.resolveMapViewById(this.locationTargetMapId)
  }

  @action.bound
  private clearLocationTarget(): void {
    this.locationTargetMapId = null
    this.locationTargetAnchor = null
    this.store.globeViewSetupStore.setAssignDialogAnchor(null)
    this.store.sitemapsSetupStore.setAssignDialogAnchor(null)
  }

  private resolveLocationAnchor(): HTMLElement | null {
    return (
      this.locationTargetAnchor ||
      this.store.globeViewSetupStore.assignDialogAnchor ||
      this.store.sitemapsSetupStore.assignDialogAnchor ||
      null
    )
  }

  private renderMapViewsSidebar(): JSX.Element {
    const { globeViewSetupStore } = this.store
    const renameTarget = this.renamingMapViewId
      ? this.resolveMapViewById(this.renamingMapViewId)
      : { globe: undefined, sitemap: undefined }
    const renameInitialName =
      renameTarget.globe?.name || renameTarget.sitemap?.name || ''
    return (
      <>
        <MapViewsSidebar
          surface="modal"
          title={Localization.translator.mapViewsManager}
          initialView={MAP_VIEWS_MANAGER_INITIAL_VIEW}
          open={this.store.isGlobeListShown}
          onOpenChange={this.handleMapViewsSidebarOpenChange}
          items={this.mapViewsSidebarItems}
          selectedId={this.selectedMapViewSidebarId}
          onSelect={this.handleSelectMapViewFromSidebar}
          canEdit
          onRename={this.handleRenameMapView}
          onDelete={this.handleDeleteMapView}
          onCopy={this.handleCopyMapView}
          onSetLocation={this.handleSetLocationForMapView}
          availableApps={this.availableApps}
          onOpenPublishTo={this.handleOpenPublishTo}
          nestedDialogPortalRef={this.nestedDialogPortalRef}
          footerSlot={
            <MapViewsSidebarCreateMenu
              onCreateView={this.handleCreateNewView}
              onCreateWhiteboard={this.handleCreateNewWhiteboard}
            />
          }
        />
        <RenameMapViewDialog
          open={!!this.renamingMapViewId}
          initialName={renameInitialName}
          onSubmit={this.submitRenameMapView}
          onCancel={this.cancelRenameMapView}
          portalContainer={this.nestedDialogPortalRef}
        />
        <LocationAssociationPopover
          open={
            (globeViewSetupStore.isAssignGlobeDialogShown ||
              this.store.sitemapsSetupStore.isAssignSitemapDialogShown) &&
            !!this.resolveLocationAnchor()
          }
          onOpenChange={open => {
            if (!open) {
              globeViewSetupStore.hideAssignGlobeDialog()
              this.store.sitemapsSetupStore.hideAssignSitemapDialog()
              this.clearLocationTarget()
            }
          }}
          anchor={this.resolveLocationAnchor()}
          mapViewItemsSetupStore={this.store.mapViewItemsSetupStore}
          globe={
            globeViewSetupStore.isAssignGlobeDialogShown
              ? this.locationTarget.globe ||
                globeViewSetupStore.selectedGlobeView
              : undefined
          }
          sitemap={
            this.store.sitemapsSetupStore.isAssignSitemapDialogShown
              ? this.locationTarget.sitemap ||
                this.store.sitemapsSetupStore.selectedSitemap
              : undefined
          }
        />
        <PublishToPopover
          open={!!this.publishToTargetId && !!this.publishToAnchor}
          onOpenChange={open => {
            if (!open) this.handleClosePublishTo()
          }}
          anchor={this.publishToAnchor}
          availableApps={this.availableApps}
          publishedTo={this.publishToCurrent}
          onApply={this.handleApplyPublishTo}
        />
      </>
    )
  }

  // Only one of `deletableGlobe` / `deletableSitemap` is set at a time.
  private renderDeleteMapViewConfirmDialog(): JSX.Element | null {
    const { globeViewsStore } = this.props
    const { globeViewSetupStore, sitemapsSetupStore, sitemapViewsSetupStore } =
      this.store
    const deletableGlobe = globeViewSetupStore.deletableGlobe
    const deletableSitemap = sitemapsSetupStore.deletableSitemap
    if (!deletableGlobe && !deletableSitemap) return null
    const target = deletableGlobe
      ? {
          name: deletableGlobe.name,
          filledImage: deletableGlobe.filledImage,
          locationAttribute:
            globeViewsStore.globeAssignedLocations(deletableGlobe)[0],
          onApply: globeViewSetupStore.deleteGlobe,
          onCancel: globeViewSetupStore.deselectDeletableGlobe,
          isDeleting: !!this.props.eventsStore?.appState.loading.get(
            e.DELETE_GLOBE_VIEW,
          ),
        }
      : {
          name: deletableSitemap.name,
          filledImage: deletableSitemap.filledImage,
          locationAttribute:
            sitemapViewsSetupStore.sitemapToLocation[deletableSitemap.id],
          onApply: sitemapsSetupStore.applySitemapDeleteConfirmDialog,
          onCancel: sitemapsSetupStore.hideSitemapDeleteConfirmDialog,
          isDeleting: !!this.props.eventsStore?.appState.loading.get(
            e.DELETE_SITEMAP,
          ),
        }
    return (
      <DeleteMapViewConfirmDialog
        open={true}
        name={target.name}
        filledImage={target.filledImage}
        locationAttribute={target.locationAttribute}
        onApply={target.onApply}
        onCancel={target.onCancel}
        isDeleting={target.isDeleting}
        // Portal into the manager modal so the confirm dialog joins the parent BaseUI focus tree.
        portalContainer={this.nestedDialogPortalRef}
      />
    )
  }

  private getCombinedMapIndex(): {
    globes: GlobeView[]
    whiteboards: Sitemap[]
    total: number
    combinedIdx: number
  } {
    const { globeViewsStore } = this.props
    const { globeViewSetupStore, sitemapsSetupStore } = this.store
    const globes = globeViewsStore.list
    const whiteboards = sitemapsSetupStore.whiteboards
    const total = globes.length + whiteboards.length
    const selectedGlobe = globeViewSetupStore.selectedGlobeView
    const selectedSitemap = sitemapsSetupStore.selectedSitemap
    const globeIdx = selectedGlobe
      ? globes.findIndex(g => g.id === selectedGlobe.id)
      : -1
    const sitemapIdx = selectedSitemap
      ? whiteboards.findIndex(s => s.id === selectedSitemap.id)
      : -1
    const combinedIdx =
      globeIdx >= 0
        ? globeIdx
        : sitemapIdx >= 0
          ? globes.length + sitemapIdx
          : -1
    return { globes, whiteboards, total, combinedIdx }
  }

  private cycleMapInSetup(direction: 1 | -1): void {
    const { globes, whiteboards, total, combinedIdx } =
      this.getCombinedMapIndex()
    if (total < 2) return

    const currentIdx = Math.max(combinedIdx, 0)
    const nextIdx = (currentIdx + direction + total) % total

    if (nextIdx < globes.length) {
      this.store.selectMapView(MapViewType.GlobeView, globes[nextIdx].id)
    } else {
      this.store.selectMapView(
        MapViewType.Whiteboard,
        whiteboards[nextIdx - globes.length].id,
      )
    }
  }

  private updateOpacity = event => {
    this.store.setOpacity(
      Math.min(
        100,
        Math.max(0, Number(event.target.value.replace(/\D/g, '')) || 0),
      ),
    )
  }

  private stepOpacity = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    const delta =
      event.key === 'ArrowUp' ? 1 : event.key === 'ArrowDown' ? -1 : 0
    if (!delta) {
      return
    }
    event.preventDefault()
    this.store.setOpacity(
      Math.min(100, Math.max(0, this.store.mapBoxEditorStore.opacity + delta)),
    )
  }

  private get viewport(): ViewState {
    return this.store.mapBoxViewerStore.viewport
  }

  @action.bound
  private async updateGlobeView(): Promise<void> {
    const { viewport, globeCorners, globeViewStyle } =
      this.store.mapBoxViewerStore
    this.isSavingGlobe = true
    try {
      const mapboxImage = await this.getMapboxImage()
      await this.store.globeViewSetupStore.updateGlobeView(
        viewport,
        globeCorners,
        null,
        null,
        globeViewStyle,
        mapboxImage,
      )
    } catch (error) {
      Sentry.captureException(error)
      toast.error(Localization.translator.failedToSaveChangesTryAgain)
    } finally {
      // Past the first await the action has ended, so the flag needs its own.
      runInAction(() => {
        this.isSavingGlobe = false
      })
    }
  }

  private async getMapboxImage(): Promise<string> {
    const mapboxBase64 = await this.store.mapBoxEditorStore.createMapImage()
    const mapboxImageFile = this.store.base64ImageToFile(
      mapboxBase64,
      this.store.globeViewSetupStore.selectedGlobeView.name,
    )
    return await this.store.sitemapsSetupStore.getImageUrl(mapboxImageFile)
  }

  @action.bound
  private async createNewView(): Promise<void> {
    this.isSavingGlobe = true
    try {
      const mapboxImage = await this.getMapboxImage()
      const {
        mapBoxViewerStore: {
          globeCorners,
          viewport,
          selectedGlobe,
          globeViewStyle,
          setViewportFromAddress,
        },
        globeViewSetupStore,
      } = this.store
      const {
        bounds,
        bearing,
        zoom,
        center: { lat, lng },
      } = this.props.eventsStore.appState.projectAddress
      const newViewport = selectedGlobe
        ? viewport
        : ({
            longitude: lng,
            latitude: lat,
            zoom,
            bearing,
            pitch: 0,
          } as ViewState)

      await globeViewSetupStore.createNewPlaceholder(
        selectedGlobe && globeCorners,
        newViewport,
        bounds,
        selectedGlobe?.items,
        selectedGlobe?.mapFiles,
        globeViewStyle,
        mapboxImage,
      )

      setViewportFromAddress()
    } catch (error) {
      Sentry.captureException(error)
      toast.error(Localization.translator.failedToSaveChangesTryAgain)
    } finally {
      runInAction(() => {
        this.isSavingGlobe = false
      })
    }
  }

  @action.bound
  private resetViewport(): void {
    this.store.mapBoxViewerStore.setViewportFromAddress()
  }
}
